import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { ContactForm } from "@/components/forms/ContactForm"
import { contactsApi, usersApi, ApiError, type ContactInput } from "@/lib/api"

type Duplicate = {
  id: string
  firstName: string
  lastName: string
  businessName: string | null
}

export default function NewContactPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [duplicates, setDuplicates] = useState<Duplicate[] | null>(null)

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const create = useMutation({
    mutationFn: (data: ContactInput) => contactsApi.create(data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["contacts"] })
      navigate(`/contacts/${res.id}`, { replace: true })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "duplicate_contact") {
        // The API returns `duplicates: [{id, firstName, lastName, businessName}]`
        // in the 409 payload. Show them so the broker can click through and
        // compare before deciding to create a new contact anyway.
        const payload = err.payload as { duplicates?: Duplicate[] } | undefined
        const list = Array.isArray(payload?.duplicates) ? payload!.duplicates : []
        setDuplicates(list)
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : "Failed to create contact")
        setDuplicates(null)
      }
    },
  })

  return (
    <AppShell title="New Contact">
      <div className="mb-4">
        <Link
          to="/contacts"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to contacts
        </Link>
      </div>

      {error && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mb-4 rounded-md border p-3 text-sm">
          <div className="font-medium">{error}</div>
          {duplicates && duplicates.length > 0 && (
            <div className="mt-2">
              <p className="text-xs">
                A contact with the same email, mobile or ABN already exists. Review the match(es) below
                before creating a new record.
              </p>
              <ul className="mt-2 space-y-1">
                {duplicates.map((d) => {
                  const label = d.businessName
                    ? `${d.businessName} — ${d.firstName} ${d.lastName}`
                    : `${d.firstName} ${d.lastName}`
                  return (
                    <li key={d.id}>
                      <Link
                        to={`/contacts/${d.id}`}
                        className="text-destructive hover:text-destructive/80 inline-flex items-center gap-1 text-sm font-medium underline"
                      >
                        View existing: {label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {duplicates && duplicates.length === 0 && (
            <p className="mt-1 text-xs">
              A contact with the same email, mobile or ABN already exists in your organisation.
              Please search first before creating a duplicate.
            </p>
          )}
        </div>
      )}

      <Card className="p-6">
        <ContactForm
          brokers={brokers.data?.users ?? []}
          onSubmit={async (data) => {
            setError(null)
            setDuplicates(null)
            await create.mutateAsync(data)
          }}
          onCancel={() => navigate("/contacts")}
          submitLabel="Create Contact"
          isSubmitting={create.isPending}
        />
      </Card>
    </AppShell>
  )
}
